// Minimal message layer. Locales bundle with the app (no runtime fetches).
// Usage: const t = useT(); t('mode.grid', 'Grid'). Keys missing from a locale
// fall back to the English string passed at the call site, so partially
// translated locales degrade gracefully. Extend the tables as strings are
// extracted; the pattern is the scaffold the v2 plan asks for.

import { useStore } from './state/store'

export type LocaleId = 'en' | 'de' | 'ja'
export const LOCALES: { id: LocaleId; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'de', label: 'Deutsch' },
  { id: 'ja', label: '日本語' },
]

const de: Record<string, string> = {
  'mode.grid': 'Raster',
  'mode.scatter': 'Streuung',
  'mode.spiral': 'Spirale',
  'mode.rings': 'Ringe',
  'mode.phyllo': 'Phyllo',
  'mode.hatch': 'Schraffur',
  'mode.voronoi': 'Voronoi',
  'mode.path': 'Pfad',
  'mode.flow': 'Fluss',
  'section.layout': 'Anordnung',
  'section.shape': 'Form',
  'section.morph': 'Morph-Ziel',
  'section.boundary': 'Umriss',
  'section.channels': 'Gradientenkanäle',
  'section.background': 'Hintergrundbild',
  'section.export': 'Exporteinstellungen',
  'channel.size': 'Größe',
  'channel.spacing': 'Abstand',
  'channel.angle': 'Winkel',
  'channel.shape': 'Form',
  'toolbar.import': 'Importieren',
  'toolbar.export': 'Exportieren',
  'toolbar.performance': 'Leistung',
  'toolbar.snap': 'Fangen',
  'toolbar.presets': 'Vorlagen',
  'status.ready': 'bereit',
  'status.generating': 'berechnet',
  'status.shortcuts': 'Taste ? für Kürzel',
  'mobile.title': 'Kleiner Bildschirm',
  'mobile.body':
    'Ventholes ist für Tablets und Desktops optimiert. Auf dem Smartphone funktioniert alles, aber die Bedienung ist beengt.',
}

const ja: Record<string, string> = {
  'mode.grid': 'グリッド',
  'mode.scatter': '散布',
  'mode.spiral': 'スパイラル',
  'mode.rings': 'リング',
  'mode.phyllo': 'フィロ',
  'mode.hatch': 'ハッチ',
  'mode.voronoi': 'ボロノイ',
  'mode.path': 'パス',
  'mode.flow': 'フロー',
  'section.layout': 'レイアウト',
  'section.shape': '形状',
  'section.morph': 'モーフ先',
  'section.boundary': '境界',
  'section.channels': 'グラデーション',
  'section.background': '背景画像',
  'section.export': '書き出し設定',
  'channel.size': 'サイズ',
  'channel.spacing': '間隔',
  'channel.angle': '角度',
  'channel.shape': '形状',
  'toolbar.import': '読み込み',
  'toolbar.export': '書き出し',
  'toolbar.performance': '高速化',
  'toolbar.snap': 'スナップ',
  'toolbar.presets': 'プリセット',
  'status.ready': '待機中',
  'status.generating': '生成中',
  'status.shortcuts': '? でショートカット',
  'mobile.title': '小さい画面',
  'mobile.body':
    'Ventholesはタブレットとデスクトップに最適化されています。スマートフォンでも動作しますが、編集画面は手狭になります。',
}

const tables: Record<LocaleId, Record<string, string>> = { en: {}, de, ja }

export function translate(locale: LocaleId, key: string, fallback: string): string {
  return tables[locale][key] ?? fallback
}

// Bound translator that re-renders its component on locale change.
export function useT(): (key: string, fallback: string) => string {
  const locale = useStore((s) => s.locale)
  return (key, fallback) => translate(locale, key, fallback)
}
